import { inject, Injectable } from '@angular/core'

import { firstValueFrom, takeUntil } from 'rxjs'

import { AppFeatures, Driver, RealtimeEventType, Resource } from '@ti/contracts'
import { PricingPlanService } from '@ti/core/common/services'
import { BaseCachedStore } from '@ti/core/models/cached-store'
import { RealtimeClient } from '@ti/core/realtime'

import { DriversManager } from './drivers.manager'

@Injectable({ providedIn: 'root' })
export class DriversStore extends BaseCachedStore<Driver> {
  protected readonly CACHE_KEY: string = 'TI_DRIVERS'
  protected readonly CHECK_RESOURCE_ACCESS = Resource.Driver
  protected readonly MAX_ITEMS = 1000

  protected readonly driversManager = inject(DriversManager)
  protected readonly pricingPlanService = inject(PricingPlanService)
  protected readonly realtimeClient = inject(RealtimeClient)

  /** A full list request returns fewer than the hard cap only when the roster is complete. */
  public isCompleteRoster(drivers: readonly Driver[]): boolean {
    return drivers.length < this.MAX_ITEMS
  }

  public async getById(id: string) {
    await this.ensureIsFresh() // Wait until data is fresh
    const drivers = await firstValueFrom(this.store.data$)
    return drivers?.find((driver) => driver.id === id)
  }

  public instantGroupDriverIdsMap(): Map<string, string[]> {
    const allDrivers = this.state.items()
    const groupDriverIdsMap = new Map<string, string[]>()

    allDrivers?.forEach((driver) => {
      driver.groupIds?.forEach((groupId) => {
        if (!groupDriverIdsMap.has(groupId)) {
          groupDriverIdsMap.set(groupId, [driver.id])
        } else {
          groupDriverIdsMap.get(groupId)?.push(driver.id)
        }
      })
    })

    return groupDriverIdsMap
  }

  protected override async loadAll() {
    const areDriversAllowed = await this.areDriversAllowed()
    if (!areDriversAllowed) return []

    return this.api.fleet.driver.list({ pageSize: this.MAX_ITEMS })
  }

  protected override async loadById(ids: string[]) {
    const areDriversAllowed = await this.areDriversAllowed()
    if (!areDriversAllowed) return []

    return this.api.fleet.driver.list({ filter: { id: ids }, pageSize: this.MAX_ITEMS })
  }

  protected override async loadSnapshot() {
    const areDriversAllowed = await this.areDriversAllowed()
    if (!areDriversAllowed) return []

    return this.api.fleet.driver.list<Pick<Driver, 'id' | 'updatedAt'>>({
      select: ['id', 'updatedAt'],
      pageSize: this.MAX_ITEMS,
    })
  }

  protected override async init() {
    await super.init()

    // Refresh store after drivers is updated
    this.driversManager.updated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids) => (ids?.length ? this.refreshById(ids) : this.refreshAll()))

    this.realtimeClient
      .subscribeToEvents(
        [
          RealtimeEventType.DriverUpdated,
          RealtimeEventType.DriverLinkedToVehicle,
          RealtimeEventType.DriverUnlinkedFromVehicle,
        ],
        (event) => {
          console.debug(`STORE DRIVERS New driver realtime refresh event:`, event)
          if (event?.data?.driverId) {
            this.refreshById(event?.data.driverId)
          }
        },
      )
      .then((unsubscribe) => this.destroy$.subscribe(unsubscribe))

    this.realtimeClient
      .subscribeToEvents(
        [RealtimeEventType.DriverCreated, RealtimeEventType.DriverDeleted],
        (event) => {
          console.debug(`STORE DRIVERS New drivers list realtime refresh event:`, event)
          this.refreshAll()
        },
      )
      .then((unsubscribe) => this.destroy$.subscribe(unsubscribe))
  }

  protected areDriversAllowed() {
    return this.pricingPlanService.areFeaturesAllowed([AppFeatures.FleetDrivers])
  }
}
